import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import moment from "moment";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import "../order/ManageOrderFilters.css";
import "./marketplaceOrderEarningsReport.css";

const PAGE_KEY = "marketplaceOrderEarningsReport";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !isNaN(date.getTime()) ? date : null;
};

const formatMoney = (n, symbol = "") =>
  `${symbol}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d) => {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format("DD MMM YYYY") : String(d);
};

const payoutBadgeClass = (status) => {
  const map = {
    Unsettled: "bg-warning text-dark",
    Pending: "bg-info text-dark",
    Approved: "bg-primary",
    Paid: "bg-success",
    Cancelled: "bg-secondary",
  };
  return map[status] || "bg-secondary";
};

const emptySummary = {
  orderCount: 0,
  grossSalesTotal: 0,
  commissionTotal: 0,
  adjustmentsTotal: 0,
  netPayableTotal: 0,
  unsettledNetTotal: 0,
  inSettlementNetTotal: 0,
};

const num = (row, camel, pascal) => Number(row[camel] ?? row[pascal] ?? 0);

/** Tags describing what this earnings row is (sale, penalty, failed delivery, etc.). */
const resolveEarningsRowTags = (row) => {
  const sale = num(row, "salePayable", "SalePayable");
  const promo = num(row, "platformPromo", "PlatformPromo");
  const wastage =
    num(row, "wastage", "Wastage") + num(row, "wastageRefund", "WastageRefund");
  const costReimb = num(row, "costReimbursement", "CostReimbursement");
  let adjustment = num(row, "adjustment", "Adjustment");
  let penalty = num(row, "penalty", "Penalty");
  const isFailedReturn = Boolean(
    row.isFailedDeliveryReturn ?? row.IsFailedDeliveryReturn ?? costReimb > 0
  );

  // Before SP redeploy, Penalty was folded into Adjustment — treat sale-less negative rows as Penalty.
  if (
    penalty === 0 &&
    sale === 0 &&
    !isFailedReturn &&
    promo === 0 &&
    wastage === 0 &&
    costReimb === 0 &&
    adjustment < 0
  ) {
    penalty = adjustment;
    adjustment = 0;
  }

  const tags = [];
  if (sale !== 0) tags.push({ key: "order", label: "Order", tone: "order" });
  if (isFailedReturn) tags.push({ key: "failed", label: "Failed delivery", tone: "failed" });
  if (penalty !== 0) tags.push({ key: "penalty", label: "Penalty", tone: "penalty" });
  if (adjustment !== 0) tags.push({ key: "adjustment", label: "Adjustment", tone: "adjustment" });
  if (promo !== 0) tags.push({ key: "promo", label: "Promo", tone: "promo" });
  if (wastage !== 0) tags.push({ key: "wastage", label: "Wastage", tone: "wastage" });
  if (costReimb !== 0 && !isFailedReturn) {
    tags.push({ key: "cost", label: "Cost reimb.", tone: "cost" });
  }
  if (tags.length === 0) tags.push({ key: "other", label: "Other", tone: "other" });
  return tags;
};

const MarketplaceOrderEarningsReport = () => {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const preferences = useSelector((state) => state.itemGridPreferences[PAGE_KEY] || {});
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const symbol = currencySymbols[getCompCurrency()] || "";

  const defaultDateRange = useMemo(
    () => [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
    []
  );

  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!stored || !Array.isArray(stored)) return defaultDateRange;
    const start = toValidDate(stored[0]);
    const end = toValidDate(stored[1]);
    if (!start || !end) return defaultDateRange;
    return [start, end];
  })();
  const [rawStartDate, rawEndDate] = dateRange;

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [searchInput, setSearchInput] = useState(preferences.searchInput || "");
  const [search, setSearch] = useState(preferences.search || "");

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, dateRange[1]];
    const nextRange = [
      newStartDate || moment().startOf("month").toDate(),
      newEndDate || moment().endOf("month").toDate(),
    ];
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: nextRange }));
  };

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSizeState] = useState(
    preferences.pageSize || { value: "20", label: "20" }
  );
  const setPageSize = (value) => {
    setPageSizeState(value);
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "pageSize", value }));
  };
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
    }
  }, [dispatch, preferences.dateRange, defaultDateRange]);

  const dateFrom = rawStartDate ? moment(rawStartDate).format("YYYY-MM-DD") : null;
  const dateTo = rawEndDate ? moment(rawEndDate).format("YYYY-MM-DD") : null;

  const applySearch = () => {
    const next = searchInput.trim();
    setSearch(next);
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "searchInput", value: searchInput }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "search", value: next }));
  };

  const fetchData = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const params = {
        CompID,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };
      if (dateFrom) params.DateFrom = dateFrom;
      if (dateTo) params.DateTo = dateTo;
      if (search) params.Search = search;

      const summaryParams = {
        CompID,
        ...(dateFrom ? { DateFrom: dateFrom } : {}),
        ...(dateTo ? { DateTo: dateTo } : {}),
        ...(search ? { Search: search } : {}),
      };

      const [listRes, summaryRes] = await Promise.all([
        axiosInstance.get(APIs.BO_GET_MARKETPLACE_PAYABLE_ORDERS, { params }),
        axiosInstance.get(APIs.BO_GET_MARKETPLACE_PAYABLE_ORDER_SUMMARY, {
          params: summaryParams,
        }),
      ]);

      if (listRes.data.status) {
        setOrders(Array.isArray(listRes.data.data) ? listRes.data.data : []);
        setTotalRecords(listRes.data.totalRecords || 0);
        setTotalPages(listRes.data.totalPages || 0);
      } else {
        showErrorToast(listRes.data.message || "Failed to load order earnings");
      }

      if (summaryRes.data.status && summaryRes.data.data) {
        const s = summaryRes.data.data;
        setSummary({
          orderCount: s.orderCount ?? s.OrderCount ?? 0,
          grossSalesTotal: s.grossSalesTotal ?? s.GrossSalesTotal ?? 0,
          commissionTotal: s.commissionTotal ?? s.CommissionTotal ?? 0,
          adjustmentsTotal: s.adjustmentsTotal ?? s.AdjustmentsTotal ?? 0,
          netPayableTotal: s.netPayableTotal ?? s.NetPayableTotal ?? 0,
          unsettledNetTotal: s.unsettledNetTotal ?? s.UnsettledNetTotal ?? 0,
          inSettlementNetTotal: s.inSettlementNetTotal ?? s.InSettlementNetTotal ?? 0,
        });
      } else {
        setSummary(emptySummary);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load order earnings");
    } finally {
      setLoading(false);
    }
  }, [CompID, pageNumber, pageSize.value, search, dateFrom, dateTo]);

  useEffect(() => {
    const handler = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(handler);
  }, [fetchData]);

  useEffect(() => {
    setPageNumber(1);
  }, [search, dateFrom, dateTo, pageSize.value]);

  const exportCsv = () => {
    if (!orders.length) {
      showErrorToast("No rows to export");
      return;
    }
    const headers = [
      "Invoice",
      "Type",
      "BusinessDate",
      "GrossSales",
      "Commission",
      "PlatformPromo",
      "Wastage",
      "WastageRefund",
      "CostReimbursement",
      "Adjustment",
      "Penalty",
      "Net",
      "PayoutStatus",
      "SettlementID",
    ];
    const lines = orders.map((r) =>
      [
        r.recvorder_InvoiceNo || r.Recvorder_InvoiceNo || "Adjustment",
        resolveEarningsRowTags(r)
          .map((t) => t.label)
          .join("; "),
        formatDate(r.businessDate || r.BusinessDate),
        num(r, "salePayable", "SalePayable").toFixed(2),
        num(r, "commission", "Commission").toFixed(2),
        num(r, "platformPromo", "PlatformPromo").toFixed(2),
        num(r, "wastage", "Wastage").toFixed(2),
        num(r, "wastageRefund", "WastageRefund").toFixed(2),
        num(r, "costReimbursement", "CostReimbursement").toFixed(2),
        num(r, "adjustment", "Adjustment").toFixed(2),
        num(r, "penalty", "Penalty").toFixed(2),
        num(r, "net", "Net").toFixed(2),
        r.payoutStatus || r.PayoutStatus || "Unsettled",
        r.settlement_ID || r.Settlement_ID || "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `marketplace-order-earnings-${dateFrom || "all"}-${dateTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const absNet = Math.abs(summary.netPayableTotal) || 1;
  const unsettledPct = Math.min(100, Math.max(0, (Math.abs(summary.unsettledNetTotal) / absNet) * 100));
  const settledPct = Math.min(100, Math.max(0, (Math.abs(summary.inSettlementNetTotal) / absNet) * 100));

  const moneyCell = (value) => {
    const n = Number(value || 0);
    return (
      <td className={`moe-num ${n === 0 ? "is-zero" : ""}`}>
        {formatMoney(n, symbol)}
      </td>
    );
  };

  return (
    <div className="moe-page">
      {loading && <Preloader />}

      <div className="d-flex align-items-start mb-3 flex-wrap gap-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">Reports</li>
            <li className="breadcrumb-item active">Marketplace Order Earnings</li>
          </ol>
          <h1 className="page-header mb-1">Marketplace Order Earnings</h1>
          <p className="text-muted mb-0 small moe-subtitle">
            Live order amounts for the dates you pick. Weekly payouts stay on{" "}
            <NavLink to="/report/marketplaceSettlementReport">Marketplace Settlements</NavLink>.
          </p>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="moe-export-btn"
            onClick={exportCsv}
            style={{ backgroundColor: themeColor }}
          >
            <i className="fa fa-download" />
            Export CSV
          </button>
        </div>
      </div>

      <div
        className="card border-0 mb-3 filter-card"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-xl-4 col-lg-5 col-md-6">
              <DateRangePicker
                startDate={rawStartDate}
                endDate={rawEndDate}
                onChange={handleDateRangeChange}
                placeholder="Select Date Range"
                className="filter-dropdown"
              />
            </div>
            <div className="col-xl-8 col-lg-7 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by invoice number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applySearch();
                  }}
                  style={{
                    backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.95)" : "rgba(255, 255, 255, 0.95)",
                    color: isDarkMode ? "#fff" : "#333",
                  }}
                />
                <button className="search-icon" type="button" onClick={applySearch} title="Search">
                  <i className="fa fa-search" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="moe-kpi-row mb-3">
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-receipt" />
            </span>
            Orders
          </div>
          <div className="moe-kpi-value">{summary.orderCount}</div>
        </div>
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-chart-line" />
            </span>
            Gross sales
          </div>
          <div className="moe-kpi-value">{formatMoney(summary.grossSalesTotal, symbol)}</div>
        </div>
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-percent" />
            </span>
            Commission
          </div>
          <div className="moe-kpi-value">{formatMoney(summary.commissionTotal, symbol)}</div>
        </div>
        <div className="moe-kpi-card moe-kpi-card--accent" style={{ backgroundColor: themeColor }}>
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-wallet" />
            </span>
            Net earned
          </div>
          <div className="moe-kpi-value">{formatMoney(summary.netPayableTotal, symbol)}</div>
        </div>
      </div>

      <div className="moe-split-card mb-3">
        <div className="moe-split-head">
          <div className="moe-split-label">Payout status for this period</div>
          <div className="moe-split-amounts">
            <span>
              Unsettled {formatMoney(summary.unsettledNetTotal, symbol)}
            </span>
            <span>
              On a settlement {formatMoney(summary.inSettlementNetTotal, symbol)}
            </span>
          </div>
        </div>
        <div className="moe-split-bar" title="Unsettled vs already on a weekly settlement">
          <span style={{ width: `${unsettledPct}%`, background: "#f0ad4e" }} />
          <span style={{ width: `${settledPct}%`, background: themeColor }} />
        </div>
      </div>

      <div className="card border-0 moe-table-card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <strong>Orders in this period</strong>
            <span className="text-muted small ms-2">
              {totalRecords} {totalRecords === 1 ? "record" : "records"}
            </span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Type</th>
                  <th className="moe-num">Gross</th>
                  <th className="moe-num">Commission</th>
                  <th className="moe-num">Adjustments</th>
                  <th className="moe-num">Cost reimb.</th>
                  <th className="moe-num">Net</th>
                  <th>Payout</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="moe-empty">
                        <i className="fa fa-inbox" />
                        No marketplace orders for this date range.
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((row, index) => {
                    const invoice = row.recvorder_InvoiceNo || row.Recvorder_InvoiceNo;
                    const status = row.payoutStatus || row.PayoutStatus || "Unsettled";
                    const settlementId = row.settlement_ID || row.Settlement_ID;
                    const costReimb = num(row, "costReimbursement", "CostReimbursement");
                    const tags = resolveEarningsRowTags(row);
                    const adjustments =
                      num(row, "platformPromo", "PlatformPromo") +
                      num(row, "wastage", "Wastage") +
                      num(row, "wastageRefund", "WastageRefund") +
                      num(row, "adjustment", "Adjustment") +
                      num(row, "penalty", "Penalty") +
                      costReimb;
                    const net = num(row, "net", "Net");
                    return (
                      <tr key={`${invoice || "adj"}-${index}`}>
                        <td>{formatDate(row.businessDate || row.BusinessDate)}</td>
                        <td>
                          <span className="moe-invoice" title={invoice || "Adjustment"}>
                            {invoice || "Adjustment"}
                          </span>
                        </td>
                        <td>
                          <div className="moe-type-tags">
                            {tags.map((tag) => (
                              <span
                                key={tag.key}
                                className={`moe-type-tag moe-type-tag--${tag.tone}`}
                              >
                                {tag.label}
                              </span>
                            ))}
                          </div>
                        </td>
                        {moneyCell(num(row, "salePayable", "SalePayable"))}
                        {moneyCell(num(row, "commission", "Commission"))}
                        {moneyCell(adjustments)}
                        {moneyCell(costReimb)}
                        <td className={`moe-num ${net === 0 ? "is-zero" : ""}`}>
                          <strong>{formatMoney(net, symbol)}</strong>
                        </td>
                        <td>
                          <span className={`badge moe-payout-badge ${payoutBadgeClass(status)}`}>
                            {status}
                          </span>
                          {settlementId ? (
                            <div className="moe-settlement-id">Settlement #{settlementId}</div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {(totalPages > 0 || totalRecords > 0) && (
          <div className="px-3 pb-3">
            <Pagination
              totalRecords={totalRecords}
              pageSize={pageSize}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              themeColor={themeColor}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceOrderEarningsReport;
